/**
 * このMarkdownProcessorの実装は、fe-dashboard, fe-portal, smart-apply-fe-consoleでそれぞれ定義しておりトリプルメンテになっている。
 * fe-coreにまとめたいところだが、unified系のライブラリがESM Onlyのため、fe-coreのESM対応、延いてはfe-portalのNext.js 13以降へのバージョンアップが必要となり、コストに見合わずこの状態としている。
 *
 * 参考：fe-coreへの統合を試みたPR https://github.com/graffer-inc/cloud-gov/pull/19943
 *
 * ３つの定義ファイル
 * - fe-dashboard：fe-dashboard/src/utils/markdown/processor.tsx
 * - fe-portal：fe-portal/src/common/utils/markdown/processor.tsx
 * - smart-apply-fe-console：smart-apply-fe-console/src/utils/markdown/processor.tsx
 */
import { unified } from "unified";
import * as production from "react/jsx-runtime";
import markdownParse from "remark-parse";
import remark2rehype from "remark-rehype";
import rehype2react from "rehype-react";
import rehypeRaw from "rehype-raw";
import { defaultSchema } from "hast-util-sanitize";
import rehypeSanitize from "rehype-sanitize";
import { visit } from "unist-util-visit";
import { convertAnchorElement } from "@graffer-inc/cloud-gov-core";
import { ExternalLink, SupportTagNames } from "@graffer-inc/console-design-kit";

/**
 * GrafferのStaffにより作成されるコンテンツを想定
 * 自由度が高め
 */
export const customUIMarkdownProcessorFactory = () =>
  unified()
    .use(markdownParse)
    .use(remark2rehype, { allowDangerousHtml: true })
    .use(rehypeRaw)
    .use(rehype2react, production);

/**
 * テナントユーザーが作成するコンテンツを想定
 * 許容できるタグを制限する機能とリンクの自動変換機能を提供する
 */
export const formMarkdownProcessorFactory = (allowedTagNames?: string[]) =>
  unified()
    .use(markdownParse)
    .use(remark2rehype, { allowDangerousHtml: true })
    .use(convertHttpStringToLink)
    .use(rehypeRaw)
    .use(rehypeSanitize, {
      ...defaultSchema,
      attributes: {
        ...defaultSchema.attributes,
        a: ["target", "rel", "href"],
      },
      tagNames: allowedTagNames ?? defaultAllowedTagNames,
    })
    .use(rehype2react, {
      ...production,
      components: {
        a: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
          <>
            {props.children ? (
              <ExternalLink
                anchor={(anchorProps) => (
                  <a {...anchorProps} href={props.href}>
                    {anchorProps.children}
                  </a>
                )}
              >
                {props.children}
              </ExternalLink>
            ) : (
              <></>
            )}
          </>
        ),
      },
    });

export const defaultAllowedTagNames = [
  "h2",
  "h3",
  "a",
  "ul",
  "ol",
  "li",
  "p",
  "strong",
] as const satisfies SupportTagNames[];

const convertHttpStringToLink = () => {
  return (tree: any) => {
    visit(tree, "text", (node, index, parent) => {
      convertAnchorElement(node, index, parent);
    });
  };
};
